import { buildImageSrc } from "./buildImageSrc";
import { extname } from "./extname";

/**
 * Parte do loader que roda nos dois lados: decide formato e caminho final e
 * guarda o resultado em memória. Não importa nada de Node — este módulo entra no
 * bundle de navegador (TD-4 em docs/tech-debts.md).
 *
 * O servidor e o navegador precisam chegar à MESMA URL para a hidratação bater;
 * por isso o cálculo mora aqui, num lugar só. E a URL não pode depender de nada
 * que só o servidor sabe (as dimensões do arquivo de origem, por exemplo): o
 * navegador não tem disco para consultar.
 */
export interface ImageLoaderParams {
  src: string;
  width: number;
  quality?: number;
  /**
   * Altura do recorte. Sem ela, o imgproxy mantém a proporção da origem. Com
   * ela, o arquivo é outro (o nome leva `LxA`): a mesma imagem com recortes
   * diferentes em lugares diferentes não pode cair no mesmo arquivo.
   */
  height?: number;
  format?: 'jpg' | 'webp' | 'avif' | 'png';
  isProduction?: boolean;
  basePath?: string;
  imgproxyPort: string;
}

/** Qualidade que não entra no nome do arquivo; qualquer outra entra (`-q100`). */
export const DEFAULT_QUALITY = 80;

export type ImagePlan =
  | { kind: 'base64'; url: string }
  | {
      kind: 'image';
      format: string;
      quality: number;
      /** Caminho do arquivo otimizado relativo a `public/`, sem basePath. */
      optimizedImagePath: string;
      /** O que vai para o HTML (com basePath quando aplicável). */
      publicPath: string;
      cacheKey: string;
    };

const objectsCache = new Map<string, string>();

export const getCachedImage = (cacheKey: string): string | undefined => objectsCache.get(cacheKey);

export const setCachedImage = (cacheKey: string, url: string): void => {
  objectsCache.set(cacheKey, url);
};

/**
 * Sufixo de dimensões do arquivo otimizado. Até a 3.x não havia sufixo: um
 * arquivo por formato, na largura da PRIMEIRA chamada, e o srcset listava esse
 * mesmo arquivo para todas as larguras — a tela de 1920px recebia o arquivo de
 * 1024px esticado, e o celular baixava o de 1024px.
 */
const sizeSuffix = (width: number, height: number | undefined, quality: number): string =>
  `-${height ? `${width}x${height}` : `${width}w`}${quality === DEFAULT_QUALITY ? '' : `-q${quality}`}`;

export function planImage({
  src,
  width,
  quality = DEFAULT_QUALITY,
  height,
  format,
  isProduction = false,
  basePath = '',
}: ImageLoaderParams): ImagePlan {
  const ext = extname(src).toLowerCase().replace('.', '');

  // Base64 não é processado
  if (/^[A-Za-z0-9+/]+={0,2}$/.test(src)) {
    return { kind: 'base64', url: buildImageSrc(src) };
  }

  // Definição automática do formato
  if (!format) {
    format = ['jpg', 'jpeg', 'png'].includes(ext) ? 'avif' : (ext as any);
  }

  const roundedWidth = Math.round(width);
  const roundedHeight = height ? Math.round(height) : undefined;
  const pathToOptimized = src.replace('/images/cms/', '/images/cms/optimized/');
  const optimizedImagePath = pathToOptimized.replace(
    /\.[^/.]+$/,
    `${sizeSuffix(roundedWidth, roundedHeight, quality)}.${format}`
  );

  return {
    kind: 'image',
    format,
    quality,
    optimizedImagePath,
    publicPath: isProduction ? `${basePath}${optimizedImagePath}` : optimizedImagePath,
    cacheKey: `${src}:${roundedWidth}:${roundedHeight}:${quality}:${isProduction}:${basePath}:${format}`,
  };
}
